import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getApplicationError } from '@oysterjs/core/graphql/errors';
import { Button } from '@oysterjs/ui/button';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '@oysterjs/ui/dialog';
import { Spinner } from '@oysterjs/ui/spinner';

import { generateSmartFillWorkspaceBucket } from '~/api/data/smart-fill/mutations';

// Each call waits on the conversion before it answers, so the next can follow soon after.
const NEXT_CALL_MS = 1_000;

const FAILED = "Smart Fill couldn't build a bucket from this workspace. Try again.";

type Generation = { state: 'idle' } | { state: 'working' } | { state: 'failed'; message: string };

type Attempt = { stopped: boolean; timer?: number };

/**
 * Opens the workspace's Smart Fill bucket, generating one from the workspace when
 * it has none. Each attempt is its own operation, so trying again after a failure
 * converts the workspace afresh. It starts from the agent's click rather than an
 * effect, which development mode would run twice and so convert twice.
 */
export const useSmartFillBucketGeneration = (workspaceId: string) => {
  const navigate = useNavigate();
  const [generation, setGeneration] = React.useState<Generation>({ state: 'idle' });
  const attempt = React.useRef<Attempt>();

  const stop = React.useCallback(() => {
    if (attempt.current) {
      attempt.current.stopped = true;
      window.clearTimeout(attempt.current.timer);
      attempt.current = undefined;
    }
  }, []);
  React.useEffect(() => stop, [stop]);

  const start = React.useCallback(() => {
    stop();
    const current: Attempt = { stopped: false };
    attempt.current = current;
    const operationId = crypto.randomUUID();
    setGeneration({ state: 'working' });

    const call = async () => {
      let message = FAILED;
      try {
        const result = await generateSmartFillWorkspaceBucket({ workspaceId, operationId });
        if (current.stopped) {
          return;
        }
        const generated = result.data?.generateSmartFillWorkspaceBucket;
        if (generated && !result.error) {
          if (!generated.completed) {
            current.timer = window.setTimeout(call, NEXT_CALL_MS);
            return;
          }
          const [only, ...more] = generated.bucketIds;
          navigate(only && more.length === 0 ? `/app/smart-fill/${only}` : '/app/smart-fill');
          return;
        }
        message = getApplicationError(result.error)?.message ?? FAILED;
      } catch {
        // A dropped connection is one more failure the agent can try again.
      }
      if (!current.stopped) {
        setGeneration({ state: 'failed', message });
      }
    };
    void call();
  }, [workspaceId, navigate, stop]);

  const cancel = React.useCallback(() => {
    stop();
    setGeneration({ state: 'idle' });
  }, [stop]);

  return { generation, start, cancel };
};

export const GenerateSmartFillBucketDialog = ({
  generation,
  start,
  cancel
}: ReturnType<typeof useSmartFillBucketGeneration>) => (
  <Dialog open={generation.state !== 'idle'} onClose={cancel} size="md">
    <DialogTitle>Smart Fill bucket</DialogTitle>
    <DialogBody>
      {generation.state === 'failed' ? (
        <p role="alert" className="text-sm/6 text-rose-600 dark:text-rose-400">
          {generation.message}
        </p>
      ) : (
        <div className="flex items-center gap-3 text-sm/6 text-neutral-600 dark:text-neutral-300">
          <Spinner size="size-4" color="sky" />
          Building a bucket for this workspace&apos;s line of business. This can take a minute.
        </div>
      )}
    </DialogBody>
    <DialogActions>
      <Button type="button" color="white" onClick={cancel}>
        {generation.state === 'failed' ? 'Close' : 'Cancel'}
      </Button>
      {generation.state === 'failed' && (
        <Button type="button" color="sky" onClick={start}>
          Try again
        </Button>
      )}
    </DialogActions>
  </Dialog>
);
